import React, {useCallback, useEffect, useState} from 'react';
import {IconButton} from '@mui/material';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import {DeleteIcon, EditIcon, TableIcon} from "../../components/Icons";
import {BaseCard} from "../../components/BaseCard";
import {Report, ReportExecutionResult} from "../../api/ReportApi";
import CircularProgress from "@mui/material/CircularProgress";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    Legend,
} from "recharts";
import Typography from "../../components/Typography";

const DEFAULT_COLORS = [
    '#FFC400',
    '#FF3F7F',
    '#8C00FF',
    '#450693',
    '#008BFF',
    '#FF2DF1',
    '#00FF9C',
];

interface ReportWidgetProps {
    report: Report;
    loading: boolean;
    height: number;
    width: number;
    onExecute: (report: Report) => Promise<ReportExecutionResult>;
    onEdit?: (report: Report) => void;
    onDelete?: (report: Report) => void;
}

export const ReportWidget: React.FC<ReportWidgetProps> = ({
                                                              report,
                                                              loading,
                                                              height,
                                                              width,
                                                              onExecute,
                                                              onEdit,
                                                              onDelete
                                                          }) => {

    const {t} = useTranslation();
    const [executionResult, setExecutionResult] = useState<ReportExecutionResult>();

    useEffect(() => {
        if (report) {
            executeReport();
        }
    }, [report]);

    const executeReport = useCallback(async () => {
        if (!report) {
            return;
        }

        const executionResult = await onExecute(report);
        setExecutionResult(executionResult);

    }, [report, onExecute]);

    // Transform unified data structure for recharts
    const chartData = executionResult?.data
        ? Object.entries(executionResult.data).map(([name, values]) => ({
            name: name.replace(/_/g, " "),
            ...values,
        }))
        : [];

    const groupings = executionResult?.groupings || [];
    const isStacked = groupings.length > 1;

    // Create color map for categories
    const colorMap: Record<string, string> = {};
    groupings.forEach((key, index) => {
        colorMap[key] = DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    });

    return (
        <BaseCard
            hoverable={false}
            className={clsx('p-5 relative overflow-hidden flex flex-col')}
            aria-label={`${t('templates.table.name')}: ${report.id}`}
            style={{ height: `${height}px`, width: `${width}px` }}
        >
            {/* Header - Fixed */}
            <div className="mb-4">
                <Typography variant={'subtitle2'} loading={loading}>
                    {report.reportName}
                </Typography>
                <p className="text-sm text-gray-600 line-clamp-2"
                   title={report.reportDescription}>
                    {report.reportDescription}
                </p>
            </div>

            {/* Chart Area - Flexible */}
            <div className="flex-1 flex items-center justify-center min-h-0">
                {loading && <CircularProgress />}

                {!loading && executionResult && chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%" className={'text-sm/6 font-medium'}>
                        <BarChart data={chartData} margin={{ left: 8, right: 8 }}>
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} width={32} />
                            <Tooltip cursor={false} />
                            {isStacked && <Legend />}
                            {groupings.map((grouping, index) => (
                                <Bar
                                    key={grouping}
                                    dataKey={grouping}
                                    stackId={isStacked ? "a" : undefined}
                                    fill={colorMap[grouping]}
                                    maxBarSize={50}
                                    activeBar={{ fillOpacity: 0.7 }}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                )}

                {!loading && !executionResult && (
                    <div className="text-gray-400">Keine Daten vorhanden</div>
                )}
            </div>

            {/* Footer - Fixed */}
            <div className="mt-4 pt-3 border-t flex items-center justify-between">
                <div className="flex items-center justify-start">
                    <IconButton
                        size="small"
                        title={t('templates.action.showData')}
                        disabled={true}
                        className="hover:bg-gray-200 transition-colors"
                    >
                        <TableIcon />
                    </IconButton>
                </div>
                <div className="flex items-center justify-end">
                    <IconButton
                        size="small"
                        title={t('templates.action.edit')}
                        disabled={true}
                        className="hover:bg-gray-200 transition-colors"
                    >
                        <EditIcon/>
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        title={t('templates.action.delete')}
                        disabled={true}
                        className="hover:bg-red-50 transition-colors"
                    >
                        <DeleteIcon/>
                    </IconButton>
                </div>
            </div>
        </BaseCard>
    );
};